import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, Barcode } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getMeta, setMeta } from "./store";
import { CATEGORIES, groupItems, matches } from "./util";
import ProductTile from "./ProductTile";
import Cart, { MobileCheckoutBar } from "./Cart";
import VariantDialog from "./VariantDialog";
import CheckoutDialog from "./CheckoutDialog";
import InvoiceDialog from "./Invoice";

export default function Register({ data, imgs, onSale, reload, goSetup }) {
    const { event, items, customers } = data;
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("All");
    const [cart, setCart] = useState([]);
    const [pick, setPick] = useState(null);
    const [checkout, setCheckout] = useState(false);
    const [invoiceId, setInvoiceId] = useState(null);
    const inputRef = useRef(null);
    const byId = useMemo(() => new Map(items.map((i) => [i.item_id, i])), [items]);
    const groups = useMemo(() => groupItems(items), [items]);
    const active = event?.status === "ACTIVE";

    useEffect(() => { if (active) getMeta("cart").then((c) => c && setCart(c)); else setCart([]); }, [event?.id, active]);
    useEffect(() => { if (active) setMeta("cart", cart); }, [cart, active]);

    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return groups.filter((g) => (cat === "All" || g.cat === cat) && (!s || g.items.some((i) => matches(i, s))));
    }, [groups, q, cat]);

    const add = (it) => {
        if (!active) return toast.error("This event is closed");
        const inCart = cart.find((l) => l.item_id === it.item_id)?.qty || 0;
        if (inCart + 1 > it.remaining_qty) return toast.error(`No more stock for ${it.sku}`);
        setCart((c) => (inCart ? c.map((l) => (l.item_id === it.item_id ? { ...l, qty: l.qty + 1 } : l)) : [...c, { item_id: it.item_id, qty: 1 }]));
    };
    const tap = (g) => (g.items.length > 1 ? setPick(g) : add(g.items[0]));
    const onEnter = () => {
        const s = q.trim().toLowerCase();
        if (!s) return;
        const exact = items.filter((i) => [i.barcode, i.sku].some((v) => v && String(v).toLowerCase() === s));
        const hits = exact.length ? exact : items.filter((i) => matches(i, s));
        if (hits.length === 1) {
            add(hits[0]);
            setQ("");
        } else if (!hits.length) toast.error(`No event item for "${q.trim()}"`);
    };

    if (!event) {
        return (
            <div className="rounded-md border border-dashed border-mist bg-white p-10 text-center" data-testid="pos-no-event">
                <p className="text-sm text-neutral-500">No active event on this device.</p>
                <button onClick={goSetup} className="mt-3 text-xs uppercase tracking-widest text-sage-dark underline" data-testid="pos-go-setup">Open Setup</button>
            </div>
        );
    }
    return (
        <div className="grid gap-4 pb-20 lg:pb-0 lg:grid-cols-[minmax(0,2.1fr)_minmax(320px,1fr)]" data-testid="pos-register">
            <section className="min-w-0 space-y-3">
                <label className="flex items-center gap-2 rounded-md border border-mist bg-white px-3 focus-within:border-sage-dark">
                    <Barcode size={20} className="text-sage-dark shrink-0" />
                    <input ref={inputRef} autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onEnter()}
                        placeholder="Search product / scan barcode" className="h-12 w-full bg-transparent text-base outline-none" data-testid="pos-search" />
                    <MagnifyingGlass size={18} className="text-neutral-400 shrink-0" />
                </label>
                <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="pos-categories">
                    {CATEGORIES.map((c) => (
                        <button key={c} onClick={() => setCat(c)} data-testid={`pos-cat-${c.toLowerCase()}`}
                            className={`rounded-full border px-3.5 py-1.5 text-xs whitespace-nowrap transition-colors ${cat === c ? "border-sage-dark bg-sage-dark text-white" : "border-mist bg-white text-neutral-600 hover:border-sage-dark"}`}>{c}</button>
                    ))}
                </div>
                {!active && <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="pos-event-closed">This event is closed. Sales are disabled.</p>}
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5" data-testid="pos-grid">
                    {shown.map((g) => <ProductTile key={g.key} g={g} img={imgs[g.items.find((i) => imgs[i.item_id])?.item_id]} disabled={!active} onTap={() => tap(g)} />)}
                </div>
                {!shown.length && <p className="text-sm text-neutral-400" data-testid="pos-no-results">No matching event products.</p>}
            </section>
            <Cart cart={cart} byId={byId} setCart={setCart} disabled={!active} onCheckout={() => setCheckout(true)} />
            <MobileCheckoutBar cart={cart} byId={byId} disabled={!active} onCheckout={() => setCheckout(true)} />
            <VariantDialog group={pick} imgs={imgs} onClose={() => setPick(null)} onPick={(it) => { add(it); setPick(null); }} />
            <CheckoutDialog open={checkout} onClose={() => setCheckout(false)} event={event} cart={cart} byId={byId} customers={customers}
                onDone={async (txn) => { setCart([]); setCheckout(false); await onSale(); setInvoiceId(txn.id); }} />
            <InvoiceDialog txn={data.txns.find((t) => t.id === invoiceId)} reload={reload} onClose={() => { setInvoiceId(null); setTimeout(() => inputRef.current?.focus(), 50); }} />
        </div>
    );
}
